import type { TemplateSet, DataRow, TemplateVariant, KeywordMatchType, CustomGroup, CampaignStructureOverride, BudgetConfig, AdDefinition } from '@/types';
import { getEffectiveAds } from '@/types';

// ─── Output types ─────────────────────────────────────────────────────────────

export interface AttemptedVariant {
  variantOrder: number;
  resolvedText: string;
  length: number;
}

export interface GeneratedField {
  slotNumber: number;
  text: string;        // final text, truncated to limit
  rawText: string;     // resolved text before truncation
  limit: number;
  truncated: boolean;  // rawText.length > limit
  missing: string[];   // {placeholder} tokens still unresolved
  variantUsed: number; // 1-indexed; 0 = no enabled variants; -1 = skipped (skip_if_too_long)
  attemptedVariants?: AttemptedVariant[]; // populated when truncated or skipped — shows all tried variants
  pinnedPosition?: number; // 1, 2, or 3 — pin to this ad position
}

export interface GeneratedKeyword {
  keywordId: string;
  text: string;
  matchTypes: KeywordMatchType[];
  missing: string[];
  variantUsed: number;
  // truncated: at least one variant overran the 80-char limit AND no later variant fit;
  // text was hard-truncated. Surfaces as a 'truncation' error in the Issues panel.
  truncated?: boolean;
  rawLength?: number;
  attemptedVariants?: AttemptedVariant[];
}

export type ErrorType = 'truncation' | 'missing_placeholder' | 'empty_slot';
export type FieldType =
  | 'headline'
  | 'description'
  | 'keyword'
  | 'path1'
  | 'path2'
  | 'url'
  | 'ad_group'
  | 'campaign';

export interface FieldError {
  type: ErrorType;
  field: FieldType;
  slotNumber?: number;
  keywordId?: string;
  message: string;
  adIndex?: number; // 1-based; undefined = shared/legacy
}

export interface GeneratedAd {
  adIndex: number; // 1-based
  headlines: GeneratedField[];
  descriptions: GeneratedField[];
  path1: string;
  path2: string;
  finalUrl: string;
}

export interface GeneratedAdGroup {
  id: string;
  rowId: string;
  rowData: DataRow;
  campaignName: string;
  adGroupName: string;
  // Legacy flat fields — always reflect ads[0] for backward compat
  headlines: GeneratedField[];
  descriptions: GeneratedField[];
  path1: string;
  path2: string;
  finalUrl: string;
  keywords: GeneratedKeyword[];
  errors: FieldError[];
  // Multi-ad: present when template has >1 ad
  ads?: GeneratedAd[];
}

export interface ExportOptions {
  skippedHeadlineSlots: Set<number>;
  skippedDescriptionSlots: Set<number>;
  skippedRows: Set<string>;
  overrides: Map<string, Map<string, string>>; // rowId → fieldKey → value
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function replacePlaceholders(
  template: string,
  row: DataRow,
  templatePlaceholders?: Record<string, string>,
): string {
  let result = template;
  // custom: prefix
  result = result.replace(/\{custom:([^}]+)\}/g, (match, key) => {
    const val = row['custom_fields']?.[key];
    return val !== undefined && val !== null ? String(val) : match;
  });
  // standard fields + template-scoped placeholders (template placeholders checked first)
  // Negative lookahead excludes unresolved {custom:...} tokens so a row column literally
  // named e.g. "custom:foo" cannot accidentally satisfy them.
  result = result.replace(/\{(?!custom:)([^}]+)\}/g, (match, key) => {
    const tpVal = templatePlaceholders?.[key];
    if (tpVal !== undefined && tpVal !== null) return String(tpVal);
    const val = row[key];
    return val !== undefined && val !== null ? String(val) : match;
  });
  return result;
}

function findMissing(text: string): string[] {
  return (text.match(/\{[^}]+\}/g) || []);
}

function stripKeywordWrap(text: string): string {
  const t = text.trim();
  if ((t.startsWith('[') && t.endsWith(']')) || (t.startsWith('"') && t.endsWith('"'))) return t.slice(1, -1);
  return t;
}

function getFieldValue(row: DataRow, fieldName: string): string {
  if (fieldName === 'none') return 'Campaign';
  if (fieldName.startsWith('custom:')) {
    const key = fieldName.replace('custom:', '');
    const val = row['custom_fields']?.[key];
    return val !== undefined && val !== null ? String(val) : 'Unknown';
  }
  const val = row[fieldName];
  return val !== undefined && val !== null ? String(val) : 'Unknown';
}

export function stripAccents(str: string): string {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// Try each variant in ascending variant_order; use the first that fits within
// the character limit. If none fit, use the first and mark as truncated.
function resolveVariants(
  variants: TemplateVariant[],
  row: DataRow,
  limit: number,
  slotNumber: number,
  templatePlaceholders?: Record<string, string>,
  skipIfTooLong?: boolean,
  pinnedPosition?: number,
): GeneratedField {
  const enabled = variants
    .filter(v => v.enabled && v.template_string.trim())
    .sort((a, b) => a.variant_order - b.variant_order);

  if (enabled.length === 0) {
    return { slotNumber, text: '', rawText: '', limit, truncated: false, missing: [], variantUsed: 0, pinnedPosition };
  }

  const attempted: AttemptedVariant[] = [];

  for (const v of enabled) {
    const raw = replacePlaceholders(v.template_string, row, templatePlaceholders);
    if (raw.length <= limit) {
      return {
        slotNumber,
        text: raw,
        rawText: raw,
        limit,
        truncated: false,
        missing: findMissing(raw),
        variantUsed: v.variant_order,
        pinnedPosition,
        // Only include attempted if we skipped earlier variants
        ...(attempted.length > 0 ? { attemptedVariants: [...attempted, { variantOrder: v.variant_order, resolvedText: raw, length: raw.length }] } : {}),
      };
    }
    attempted.push({ variantOrder: v.variant_order, resolvedText: raw, length: raw.length });
  }

  // No variant fits — skip slot entirely or use first and truncate
  if (skipIfTooLong) {
    return {
      slotNumber,
      text: '',
      rawText: attempted[0].resolvedText,
      limit,
      truncated: false,
      missing: [],
      variantUsed: -1,
      pinnedPosition,
      attemptedVariants: attempted,
    };
  }

  return {
    slotNumber,
    text: attempted[0].resolvedText.substring(0, limit),
    rawText: attempted[0].resolvedText,
    limit,
    truncated: true,
    missing: findMissing(attempted[0].resolvedText),
    variantUsed: enabled[0].variant_order,
    pinnedPosition,
    attemptedVariants: attempted,
  };
}

function truncationMessage(field: GeneratedField): string {
  const base = `${field.rawText.length} chars - ${field.rawText.length - field.limit} over limit`;
  if (field.attemptedVariants && field.attemptedVariants.length > 1) {
    const tried = field.attemptedVariants
      .map(v => `V${v.variantOrder}: ${v.length}/${field.limit}`)
      .join(', ');
    return `${base} (tried ${tried})`;
  }
  return base;
}

function collectAdErrors(headlines: GeneratedField[], descriptions: GeneratedField[], finalUrl: string, adIndex?: number): FieldError[] {
  const errors: FieldError[] = [];

  for (const h of headlines) {
    if (h.variantUsed === -1) continue;
    if (h.variantUsed === 0) {
      errors.push({ type: 'empty_slot', field: 'headline', slotNumber: h.slotNumber, message: 'No enabled variants', adIndex });
    } else {
      if (h.truncated) {
        errors.push({ type: 'truncation', field: 'headline', slotNumber: h.slotNumber, message: truncationMessage(h), adIndex });
      }
      if (h.missing.length > 0) {
        errors.push({ type: 'missing_placeholder', field: 'headline', slotNumber: h.slotNumber, message: `Unresolved: ${h.missing.join(', ')}`, adIndex });
      }
    }
  }

  for (const d of descriptions) {
    if (d.variantUsed === -1) continue;
    if (d.variantUsed === 0) {
      errors.push({ type: 'empty_slot', field: 'description', slotNumber: d.slotNumber, message: 'No enabled variants', adIndex });
    } else {
      if (d.truncated) {
        errors.push({ type: 'truncation', field: 'description', slotNumber: d.slotNumber, message: truncationMessage(d), adIndex });
      }
      if (d.missing.length > 0) {
        errors.push({ type: 'missing_placeholder', field: 'description', slotNumber: d.slotNumber, message: `Unresolved: ${d.missing.join(', ')}`, adIndex });
      }
    }
  }

  const urlMissing = findMissing(finalUrl);
  if (urlMissing.length > 0) {
    errors.push({ type: 'missing_placeholder', field: 'url', message: `Unresolved: ${urlMissing.join(', ')}`, adIndex });
  }

  return errors;
}

function collectErrors(ag: Omit<GeneratedAdGroup, 'errors'>): FieldError[] {
  const errors: FieldError[] = [];

  if (ag.ads && ag.ads.length > 0) {
    for (const ad of ag.ads) {
      errors.push(...collectAdErrors(ad.headlines, ad.descriptions, ad.finalUrl, ad.adIndex));
    }
  } else {
    errors.push(...collectAdErrors(ag.headlines, ag.descriptions, ag.finalUrl));
  }

  for (const kw of ag.keywords) {
    if (kw.truncated && kw.rawLength != null) {
      const over = kw.rawLength - 80;
      const tried = kw.attemptedVariants && kw.attemptedVariants.length > 1
        ? ` (tried ${kw.attemptedVariants.map(v => `V${v.variantOrder}: ${v.length}/80`).join(', ')})`
        : '';
      errors.push({ type: 'truncation', field: 'keyword', keywordId: kw.keywordId, message: `${kw.rawLength} chars - ${over} over limit${tried}` });
    }
    if (kw.missing.length > 0) {
      errors.push({ type: 'missing_placeholder', field: 'keyword', keywordId: kw.keywordId, message: `Unresolved: ${kw.missing.join(', ')}` });
    }
  }

  return errors;
}



function resolveKeywordVariants(
  kw: { id: string; variants: TemplateVariant[]; match_types: KeywordMatchType[] },
  row: DataRow,
  templatePlaceholders?: Record<string, string>,
): GeneratedKeyword[] {
  const enabled = kw.variants
    .filter(v => v.enabled && v.template_string.trim())
    .sort((a, b) => a.variant_order - b.variant_order);
  if (enabled.length === 0) return [];

  const attempted: AttemptedVariant[] = [];
  let chosen = enabled[0];
  let fits = false;
  for (const v of enabled) {
    const resolved = stripKeywordWrap(replacePlaceholders(v.template_string, row, templatePlaceholders));
    attempted.push({ variantOrder: v.variant_order, resolvedText: resolved, length: resolved.length });
    if (resolved.length <= 80) { chosen = v; fits = true; break; }
  }
  const rawText = stripKeywordWrap(replacePlaceholders(chosen.template_string, row, templatePlaceholders));
  const text = rawText.substring(0, 80);
  return [{
    keywordId: kw.id,
    text,
    matchTypes: kw.match_types,
    missing: findMissing(text),
    variantUsed: chosen.variant_order,
    truncated: !fits && rawText.length > 80,
    rawLength: rawText.length,
    ...(attempted.length > 1 ? { attemptedVariants: attempted } : {}),
  }];
}

// ─── Main generation ──────────────────────────────────────────────────────────

export function generateAdGroups(
  templateSet: TemplateSet,
  rows: DataRow[],
  structureOverride?: CampaignStructureOverride,
): GeneratedAdGroup[] {
  const tc = templateSet.template_content;
  const tp = { ...tc.template_placeholders, template_name: templateSet.name };
  const primaryGrouping = structureOverride?.primary_grouping_field
    ?? (tc.primary_grouping_field || tc.campaign_grouping_field || 'id');
  const secondaryGrouping = structureOverride?.secondary_grouping_field
    ?? tc.secondary_grouping_field;
  const campaignNameTpl = structureOverride?.campaign_name_template
    ?? (tc.campaign_name_template || '');
  const adGroupNameTpl = structureOverride?.ad_group_name_template
    ?? (tc.ad_group_name_template || '');

  // Group rows by campaign key
  const campaignGroups = new Map<string, { rows: DataRow[]; firstRow: DataRow }>();
  for (const row of rows) {
    const primary = getFieldValue(row, primaryGrouping);
    const campaignKey = secondaryGrouping
      ? `${primary}__${getFieldValue(row, secondaryGrouping)}`
      : primary;
    if (!campaignGroups.has(campaignKey)) {
      campaignGroups.set(campaignKey, { rows: [], firstRow: row });
    }
    campaignGroups.get(campaignKey)!.rows.push(row);
  }

  const adGroups: GeneratedAdGroup[] = [];
  let adGroupCounter = 0;

  campaignGroups.forEach(({ rows: groupRows, firstRow }) => {
    const campaignName = replacePlaceholders(campaignNameTpl, firstRow, tp);

    for (const row of groupRows) {
      const adGroupName = replacePlaceholders(adGroupNameTpl, row, tp);

      const effectiveAds = getEffectiveAds(tc);
      const generatedAds: GeneratedAd[] = effectiveAds.map(adDef => {
        const headlines = adDef.headlines.map(slot => resolveVariants(slot.variants, row, 30, slot.slot_number, tp, slot.skip_if_too_long, slot.pinned_position));
        const descriptions = adDef.descriptions.map(slot => resolveVariants(slot.variants, row, 90, slot.slot_number, tp, slot.skip_if_too_long, slot.pinned_position));
        const adFinalUrl = tc.use_per_ad_urls && adDef.final_url_template != null
          ? adDef.final_url_template : tc.final_url_template || '';
        const adPath1 = tc.use_per_ad_urls && adDef.path1_template != null
          ? adDef.path1_template : tc.path1_template || '';
        const adPath2 = tc.use_per_ad_urls && adDef.path2_template != null
          ? adDef.path2_template : tc.path2_template || '';
        return {
          adIndex: adDef.ad_index,
          headlines,
          descriptions,
          path1: replacePlaceholders(adPath1, row, tp).substring(0, 15),
          path2: replacePlaceholders(adPath2, row, tp).substring(0, 15),
          finalUrl: replacePlaceholders(adFinalUrl, row, tp),
        };
      });

      const keywords: GeneratedKeyword[] = tc.keywords.flatMap(kw => resolveKeywordVariants(kw, row, tp));

      const ad1 = generatedAds[0];
      const agId = `ag_${adGroupCounter++}`;
      const base = {
        id: agId,
        rowId: agId,
        rowData: row,
        campaignName,
        adGroupName,
        headlines: ad1.headlines,
        descriptions: ad1.descriptions,
        path1: ad1.path1,
        path2: ad1.path2,
        finalUrl: ad1.finalUrl,
        keywords,
        ...(generatedAds.length > 1 ? { ads: generatedAds } : {}),
      };

      adGroups.push({ ...base, errors: collectErrors(base) });
    }
  });

  return adGroups;
}

// ─── CSV export ───────────────────────────────────────────────────────────────

function escapeCSV(value: string): string {
  if (!value) return '';
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function buildCSV(
  adGroups: GeneratedAdGroup[],
  templateSet: TemplateSet,
  options: ExportOptions,
  structureOverride?: CampaignStructureOverride,
  budgetConfig?: BudgetConfig,
): string {
  const { skippedHeadlineSlots, skippedDescriptionSlots, skippedRows, overrides } = options;
  const generateAccentStripped = templateSet.template_content.generate_accent_stripped_keywords;
  const campaignStatus = structureOverride?.campaign_status ?? templateSet.template_content.campaign_status;
  const adGroupStatus = structureOverride?.ad_group_status ?? templateSet.template_content.ad_group_status;

  const hasBudget = budgetConfig?.enabled;

  const lines: string[] = [];
  const headerCols = [
    'Campaign', 'Ad Group', 'Ad Type',
    ...Array.from({ length: 15 }, (_, i) => `Headline ${i + 1}`),
    ...Array.from({ length: 15 }, (_, i) => `Headline ${i + 1} position`),
    ...Array.from({ length: 4 }, (_, i) => `Description ${i + 1}`),
    ...Array.from({ length: 4 }, (_, i) => `Description ${i + 1} position`),
    'Path 1', 'Path 2', 'Final URL', 'Keyword', 'Match Type',
    'Campaign Status', 'Ad Group Status',
    ...(hasBudget ? ['Campaign Daily Budget'] : []),
    'Search Network', 'Search Partners', 'Display Network',
    'Excludes EU Political Ads',
  ];
  lines.push(headerCols.join(','));

  // Pad for non-campaign rows: empty values for trailing columns after Ad Group Status
  // Without budget: Search Network, Search Partners, Display Network, EU Ads = 4 empty
  // With budget: Campaign Daily Budget, Search Network, Search Partners, Display Network, EU Ads = 5 empty
  const emptyTrailingCols = hasBudget ? ',,,,,' : ',,,,';

  const seenCampaigns = new Set<string>();

  for (const ag of adGroups) {
    if (skippedRows.has(ag.rowId)) continue;

    const rowOv = overrides.get(ag.rowId) ?? new Map<string, string>();
    const ov = (key: string, fallback: string) => rowOv.get(key) ?? fallback;

    const campaignName = ov('campaign', ag.campaignName);
    const adGroupName = ov('ad_group', ag.adGroupName);

    // Campaign-level row (emitted once per unique campaign)
    if (!seenCampaigns.has(campaignName)) {
      seenCampaigns.add(campaignName);
      let budgetVal = '';
      if (hasBudget) {
        if (budgetConfig.mode === 'uniform' && budgetConfig.uniformAmount != null) {
          budgetVal = String(budgetConfig.uniformAmount);
        } else if (budgetConfig.mode === 'per_campaign' && budgetConfig.perCampaignAmounts) {
          // Try the override-resolved name first, then fall back to the original campaign
          // name (preset was keyed against the pre-override name).
          const amt = budgetConfig.perCampaignAmounts[campaignName]
            ?? budgetConfig.perCampaignAmounts[ag.campaignName];
          if (amt != null) budgetVal = String(amt);
        }
      }
      const campaignRow = [
        escapeCSV(campaignName), '', '',
        ...Array(15).fill(''), ...Array(15).fill(''), // headlines + positions
        ...Array(4).fill(''), ...Array(4).fill(''),   // descriptions + positions
        '', '', '', '', '',
        campaignStatus, '',
        ...(hasBudget ? [budgetVal] : []),
        'enabled', 'disabled', 'disabled',
        'Yes',
      ].join(',');
      lines.push(campaignRow);
    }

    // RSA rows — one per ad in the ad group
    const adsToExport: Array<{ headlines: GeneratedField[]; descriptions: GeneratedField[]; path1: string; path2: string; finalUrl: string; adIndex?: number }> =
      ag.ads && ag.ads.length > 0
        ? ag.ads.map(a => ({ ...a }))
        : [{ headlines: ag.headlines, descriptions: ag.descriptions, path1: ag.path1, path2: ag.path2, finalUrl: ag.finalUrl }];

    for (const ad of adsToExport) {
      const prefix = ad.adIndex != null ? `a${ad.adIndex}:` : '';
      const path1 = ov(`${prefix}path1`, ad.path1);
      const path2 = ov(`${prefix}path2`, ad.path2);
      const finalUrl = ov(`${prefix}url`, ad.finalUrl);
      const headlines = ad.headlines.map(h =>
        skippedHeadlineSlots.has(h.slotNumber) ? '' : ov(`${prefix}h${h.slotNumber}`, h.text),
      );
      const descriptions = ad.descriptions.map(d =>
        skippedDescriptionSlots.has(d.slotNumber) ? '' : ov(`${prefix}d${d.slotNumber}`, d.text),
      );

      const headlinePositions = ad.headlines.map(h =>
        h.pinnedPosition ? String(h.pinnedPosition) : '',
      );
      const descriptionPositions = ad.descriptions.map(d =>
        d.pinnedPosition ? String(d.pinnedPosition) : '',
      );

      lines.push([
        escapeCSV(campaignName), escapeCSV(adGroupName), 'Responsive search ad',
        ...headlines.map(escapeCSV),
        ...headlinePositions,
        ...descriptions.map(escapeCSV),
        ...descriptionPositions,
        escapeCSV(path1), escapeCSV(path2), escapeCSV(finalUrl),
        '', '',
        campaignStatus,
        adGroupStatus,
      ].join(',') + emptyTrailingCols);
    }

    // Keyword rows
    for (const kw of ag.keywords) {
      const kwText = ov(`kw_${kw.keywordId}`, kw.text);
      for (const matchType of kw.matchTypes) {
        const kwRow = [
          escapeCSV(campaignName), escapeCSV(adGroupName), '',
          ...Array(15).fill(''), ...Array(15).fill(''), // headlines + positions
          ...Array(4).fill(''), ...Array(4).fill(''),   // descriptions + positions
          '', '', '',
          escapeCSV(kwText),
          matchType.charAt(0).toUpperCase() + matchType.slice(1),
          campaignStatus,
          adGroupStatus,
        ].join(',') + emptyTrailingCols;
        lines.push(kwRow);

        // Accent-stripped duplicate
        if (generateAccentStripped) {
          const stripped = stripAccents(kwText);
          if (stripped !== kwText) {
            lines.push([
              escapeCSV(campaignName), escapeCSV(adGroupName), '',
              ...Array(15).fill(''), ...Array(15).fill(''),
              ...Array(4).fill(''), ...Array(4).fill(''),
              '', '', '',
              escapeCSV(stripped),
              matchType.charAt(0).toUpperCase() + matchType.slice(1),
              campaignStatus,
              adGroupStatus,
            ].join(',') + emptyTrailingCols);
          }
        }
      }
    }
  }

  return lines.join('\n');
}

// ─── Custom group generation ──────────────────────────────────────────────────

export function generateCustomGroupAdGroups(
  templateSet: TemplateSet,
  customGroups: CustomGroup[],
  structureOverride?: CampaignStructureOverride,
): GeneratedAdGroup[] {
  const tc = templateSet.template_content;
  const tp = { ...tc.template_placeholders, template_name: templateSet.name };
  const adGroupNameTpl = structureOverride?.ad_group_name_template
    ?? (tc.ad_group_name_template || '');
  const campaignNameTpl = structureOverride?.campaign_name_template
    ?? (tc.campaign_name_template || '');
  const adGroups: GeneratedAdGroup[] = [];
  let counter = 0;

  for (const group of customGroups) {
    if (group.rows.length === 0) continue;
    const firstRow = group.rows[0];
    // Custom group override > structure override > template default
    const campaignName = group.campaignNameOverride?.trim()
      ? replacePlaceholders(group.campaignNameOverride.trim(), firstRow, tp)
      : replacePlaceholders(campaignNameTpl, firstRow, tp);

    for (const row of group.rows) {
      const adGroupName = replacePlaceholders(adGroupNameTpl, row, tp);

      const effectiveAds = getEffectiveAds(tc);
      const generatedAds: GeneratedAd[] = effectiveAds.map(adDef => {
        const headlines = adDef.headlines.map(slot => resolveVariants(slot.variants, row, 30, slot.slot_number, tp, slot.skip_if_too_long, slot.pinned_position));
        const descriptions = adDef.descriptions.map(slot => resolveVariants(slot.variants, row, 90, slot.slot_number, tp, slot.skip_if_too_long, slot.pinned_position));
        const adFinalUrl = tc.use_per_ad_urls && adDef.final_url_template != null
          ? adDef.final_url_template : tc.final_url_template || '';
        const adPath1 = tc.use_per_ad_urls && adDef.path1_template != null
          ? adDef.path1_template : tc.path1_template || '';
        const adPath2 = tc.use_per_ad_urls && adDef.path2_template != null
          ? adDef.path2_template : tc.path2_template || '';
        return {
          adIndex: adDef.ad_index,
          headlines,
          descriptions,
          path1: replacePlaceholders(adPath1, row, tp).substring(0, 15),
          path2: replacePlaceholders(adPath2, row, tp).substring(0, 15),
          finalUrl: replacePlaceholders(adFinalUrl, row, tp),
        };
      });

      const keywords: GeneratedKeyword[] = tc.keywords.flatMap(kw => resolveKeywordVariants(kw, row, tp));

      const ad1 = generatedAds[0];
      const agId = `cg_${counter++}`;
      const base = {
        id: agId, rowId: agId, rowData: row,
        campaignName, adGroupName,
        headlines: ad1.headlines,
        descriptions: ad1.descriptions,
        path1: ad1.path1,
        path2: ad1.path2,
        finalUrl: ad1.finalUrl,
        keywords,
        ...(generatedAds.length > 1 ? { ads: generatedAds } : {}),
      };
      adGroups.push({ ...base, errors: collectErrors(base) });
    }
  }
  return adGroups;
}
